import { useEffect, useState } from 'react';
import { Wrench, Clock, RefreshCw } from 'lucide-react';

interface MaintenanceOverlayProps {
  maintenanceEndsAt: string;
  maintenanceMessage?: string;
}

export function MaintenanceOverlay({ maintenanceEndsAt, maintenanceMessage }: MaintenanceOverlayProps) {
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [currentTip, setCurrentTip] = useState<number>(0);

  const maintenanceTips = [
    'Taking a coffee break?',
    'Stretching your legs is good for productivity',
    'This is a great time to plan your next tasks',
    'We appreciate your patience',
    'Almost there, hang tight!',
  ];

  useEffect(() => {
    const calculateTimeRemaining = () => {
      const endTime = new Date(maintenanceEndsAt);
      const now = new Date();
      const remaining = Math.max(0, Math.floor((endTime.getTime() - now.getTime()) / 1000));
      setTimeRemaining(remaining);

      if (remaining === 0) {
        window.location.reload();
      }
    };

    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, [maintenanceEndsAt]);

  useEffect(() => {
    const tipInterval = setInterval(() => {
      setCurrentTip((prev) => (prev + 1) % maintenanceTips.length);
    }, 8000);

    return () => clearInterval(tipInterval);
  }, []);

  const hours = Math.floor(timeRemaining / 3600);
  const minutes = Math.floor((timeRemaining % 3600) / 60);
  const seconds = timeRemaining % 60;

  return (
    <div className="fixed inset-0 z-[9999] bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-30" />

      <div className="relative max-w-2xl mx-auto px-6 text-center animate-fade-in">
        <div className="relative mb-8">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-32 h-32 bg-blue-500 rounded-full blur-3xl opacity-30 animate-pulse" />
          </div>
          <div className="relative">
            <Wrench className="w-24 h-24 mx-auto text-blue-400 animate-bounce-slow" />
          </div>
        </div>

        <h1 className="text-4xl font-bold text-white mb-4">
          System Maintenance
        </h1>

        <p className="text-xl text-blue-200 mb-8">
          {maintenanceMessage || 'We are currently performing scheduled maintenance to improve your experience'}
        </p>

        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 mb-6 border border-white/20 shadow-2xl">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Clock className="w-6 h-6 text-blue-300" />
            <p className="text-blue-200 text-lg font-medium">
              Estimated time remaining
            </p>
          </div>

          <div className="flex items-center justify-center gap-4 mb-6">
            {hours > 0 && (
              <div className="text-center">
                <div className="text-5xl font-bold text-white font-mono bg-white/10 rounded-xl px-6 py-4 min-w-[100px]">
                  {hours.toString().padStart(2, '0')}
                </div>
                <div className="text-blue-300 text-sm mt-2 uppercase tracking-wider">
                  Hours
                </div>
              </div>
            )}

            <div className="text-center">
              <div className="text-5xl font-bold text-white font-mono bg-white/10 rounded-xl px-6 py-4 min-w-[100px]">
                {minutes.toString().padStart(2, '0')}
              </div>
              <div className="text-blue-300 text-sm mt-2 uppercase tracking-wider">
                Minutes
              </div>
            </div>

            <div className="text-center">
              <div className="text-5xl font-bold text-white font-mono bg-white/10 rounded-xl px-6 py-4 min-w-[100px]">
                {seconds.toString().padStart(2, '0')}
              </div>
              <div className="text-blue-300 text-sm mt-2 uppercase tracking-wider">
                Seconds
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 text-blue-300">
            <RefreshCw className="w-4 h-4 animate-spin-slow" />
            <p className="text-sm">
              {maintenanceTips[currentTip]}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 text-blue-300 text-sm">
          <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
          <p>The page will automatically refresh when maintenance is complete</p>
        </div>
      </div>

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.8s ease-out;
        }

        .animate-bounce-slow {
          animation: bounce-slow 3s ease-in-out infinite;
        }

        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }
      `}</style>
    </div>
  );
}